import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { observable, ObservableSet, runInAction } from 'mobx';
import { useStore } from 'app/context';
import { Table } from 'app/components/table/table';
import { ITableColumn, TableModel, TableRow } from 'app/components/table/table.model';
import { Modal } from 'app/components/Modal';
import { Button } from 'app/components/common/Button';
import { CheckboxControl } from 'app/components/common/CheckboxControl';
import { Icon } from 'app/components/common/Icon';
import { TooltipPortal } from 'app/components/common/TooltipPortal';
import { FileTypeIcon } from 'app/components/common/FileTypeIcon';
import { FileSize } from 'app/components/common/FileSize';
import { Jpg25LeitfadenButton } from 'app/components/documents/jpg/JpgFocusThemen';
import { JpgFocusThemenDef2026 } from 'app/components/documents/jpg/2026/JpgFocusThemenDef2026';
import { IJpgThemaDef } from 'app/stores/ui/jpg.themen.ui.store';
import { formatDateLong } from 'app/utils';
import { ASP_YEAR, AspImpuls, aspImpulsModifier, aspKategorieModifier, buildAspPotenzialListe, campaignsByImpuls, formatAspDateShort, IAspAgtPotenzial } from './asp.mock.data';
import { AspPotenzialZelle } from './AgenturASPModal';

const aspThemaModalId = 'design.jpgAspThemaModal';

interface IJpgThemaMockRow {
	key: string;
	// echte Themendefinition aus JpgFocusThemenDef2026
	def: IJpgThemaDef;
	adzEmpfehlung?: boolean;
	geteiltMit?: number;
	// ASP-Themen sind mit einem Impuls verknüpft (Impulse überlappen die Kampagnen-Kategorien)
	impuls?: AspImpuls;
}

const themaDef = (keyThema: string): IJpgThemaDef => JpgFocusThemenDef2026.find((d) => d.keyThema === keyThema)!;

// typische Themenauswahl eines VBL-JPGs plus die drei ASP-Fokusthemen
const MOCK_THEMEN: IJpgThemaMockRow[] = [
	{ key: 'uebergabe', def: themaDef('uebergabe'), geteiltMit: 2 },
	{ key: 'personaluebernahme', def: themaDef('personaluebernahme'), geteiltMit: 2 },
	{ key: 'sts', def: themaDef('sts'), adzEmpfehlung: true, geteiltMit: 3 },
	{ key: 'amis', def: themaDef('amis'), geteiltMit: 1 },
	{ key: 'leben', def: themaDef('leben') },
	{ key: 'aspVertriebsimpulse', def: themaDef('aspVertriebsimpulse'), impuls: 'Automatisierter Vertriebsimpuls', geteiltMit: 2 },
	{ key: 'aspSteuerungsimpulse', def: themaDef('aspSteuerungsimpulse'), impuls: 'Aktivierender Steuerungsimpuls' },
	{ key: 'aspEntwicklungsimpulse', def: themaDef('aspEntwicklungsimpulse'), impuls: 'Entwicklungsimpuls' },
];

const MOCK_ERSTELLT = new Date(ASP_YEAR, 0, 5, 13, 0);
const MOCK_GEAENDERT = new Date(ASP_YEAR, 1, 27, 10, 49);

interface IMockShare {
	steart: string;
	name: string;
}

const MOCK_SHARED_WITH: IMockShare[] = [
	{ steart: 'KME', name: 'Thomas Muster' },
	{ steart: 'BSG', name: 'Maria Beispiel' },
	{ steart: 'EDT', name: 'Max Mustermann' },
	{ steart: 'LPV', name: 'Erika Musterfrau' },
	{ steart: 'EDT', name: 'Julia Beispiel' },
];

interface IMockUpload {
	title: string;
	mime: string;
	fileSize: number;
}

const MOCK_UPLOADS: IMockUpload[] = [
	{ title: 'JPG Musteragentur 2026.pdf', mime: 'application/pdf', fileSize: 1470000 },
	{ title: 'Notizen Jahresgespräch.jpeg', mime: 'image/jpeg', fileSize: 139980 },
];

const themaPotenzial = (row: IJpgThemaMockRow, vm: IAspAgtPotenzial) => {
	if (!row.impuls) {
		return undefined;
	}
	return campaignsByImpuls(row.impuls).reduce((sum, c) => sum + vm.potenziale[c.id], 0);
};

const JpgMockActions = () => {
	return (
		<div className="document-actions">
			<Jpg25LeitfadenButton />
			<Button className="button is-icon is-small" data-tooltip="Exportieren" data-tooltip-position="bottom">
				<Icon iconClass="download" />
			</Button>
			<Button className="button is-icon is-small" data-tooltip="Per E-Mail versenden" data-tooltip-position="bottom">
				<Icon iconClass="share-all" />
			</Button>
			<Button className="button is-icon is-small" data-tooltip="Teilen" data-tooltip-position="bottom">
				<Icon iconClass="share-alt" />
			</Button>
			<Button className="button is-icon is-small is-readonly is-grey" data-tooltip="Bearbeitbar" data-tooltip-position="bottom">
				<Icon iconClass="unlock-alt" />
			</Button>
		</div>
	);
};

const JpgMockUploads = () => {
	return (
		<div className="document-uploads">
			<div className="uploads-title">
				<Icon iconClass="paperclip" />
				<span>Anhänge</span>
				<span className="tag is-marginless is-small">{MOCK_UPLOADS.length}</span>
			</div>
			<div className="file-uploader is-inline">
				<div className="drop-area">
					<div className="message">
						<div className="body">
							<div className="icon">
								<Icon iconClass="plus-circle" />
							</div>
							<div className="text">Datei hinzufügen</div>
						</div>
					</div>
				</div>
			</div>
			<div className="upload-items">
				{MOCK_UPLOADS.map((u) => (
					<div className="document-upload" key={u.title}>
						<Button className="button is-blank download-button">
							<FileTypeIcon mime={u.mime} />
							<div className="file-name">{u.title}</div>
							<FileSize fileSize={u.fileSize} />
						</Button>
						<Button className="button is-icon is-small delete-button">
							<Icon iconClass="trash" />
						</Button>
					</div>
				))}
			</div>
		</div>
	);
};

export const AspJpgIntegration = observer(() => {
	const { uiStore } = useStore();
	const vm = useMemo(() => buildAspPotenzialListe()[0], []);
	const [selectedThema, setSelectedThema] = useState<IJpgThemaMockRow | undefined>();

	// vom VBL ausgewählte Fokus-Kampagnen der Agentur (Design-Mock: mit den Top-5-Kampagnen vorbelegt)
	const fokusKampagnen = useMemo(() => observable.set<string>(vm.top5), [vm]);

	const onToggleFokusKampagne = (campaignId: string) => {
		runInAction(() => {
			if (fokusKampagnen.has(campaignId)) {
				fokusKampagnen.delete(campaignId);
			} else {
				fokusKampagnen.add(campaignId);
			}
		});
	};

	const tm = useMemo(() => {
		const tm = new TableModel<IJpgThemaMockRow, string>();
		const cols: ITableColumn<IJpgThemaMockRow, string>[] = [];

		cols.push({
			label: '',
			path: 'def.background',
			cellModifier: 'is-graphic-cell',
			labelModifier: 'is-graphic-cell',
			render: (row: TableRow<IJpgThemaMockRow>) => <div className="color-bar" style={{ backgroundColor: row.data.def.background || '' }}></div>,
		});

		cols.push({
			label: 'Gruppe',
			path: 'def.group',
			cellModifier: 'group-cell',
			render: (row: TableRow<IJpgThemaMockRow>) => (
				<div className="align-vertical">
					<strong>{row.data.def.group}</strong>{' '}
					{row.data.adzEmpfehlung && (
						<TooltipPortal tooltip="Empfehlung Fitnessplan" position="right">
							<Icon iconClass="star" faModifier="color-warning" />
						</TooltipPortal>
					)}
				</div>
			),
		});

		cols.push({
			label: 'Thema',
			path: 'def.thema',
			cellModifier: 'thema-cell',
			render: (row: TableRow<IJpgThemaMockRow>) => (
				<>
					<strong>{row.data.def.thema}</strong> <br />
					<span className="color-grey print-none">{row.data.def.byLine}</span>
				</>
			),
		});

		cols.push({
			label: 'Potenzial',
			path: 'impuls',
			render: (row: TableRow<IJpgThemaMockRow>) => {
				const pot = themaPotenzial(row.data, vm);
				if (pot === undefined) {
					return <span className="color-grey">—</span>;
				}
				const campaigns = campaignsByImpuls(row.data.impuls!);
				const imFokus = campaigns.filter((c) => fokusKampagnen.has(c.id)).length;
				return (
					<>
						<strong>{pot}</strong>
						<br />
						<small className="color-grey">
							{imFokus} von {campaigns.length} Kampagnen im Fokus
						</small>
					</>
				);
			},
		});

		cols.push({
			label: '',
			path: 'geteiltMit',
			cellModifier: 'thema-share',
			render: (row: TableRow<IJpgThemaMockRow>) => (
				<>
					{row.data.geteiltMit && (
						<TooltipPortal tooltip={`Geteilt mit ${row.data.geteiltMit}`} position="right">
							<Icon iconClass="user-friends" />
						</TooltipPortal>
					)}
				</>
			),
		});

		tm.onRowClick = (row: TableRow<IJpgThemaMockRow>) => {
			if (!row.data.impuls) {
				return;
			}
			setSelectedThema(row.data);
			uiStore.showModal(aspThemaModalId);
		};

		tm.setCols(cols);
		tm.sortDisabled = true;
		tm.idProperty = 'key';
		tm.idType = 'string';
		return tm;
	}, [uiStore, vm, fokusKampagnen]);

	useEffect(() => {
		tm.setRowData(MOCK_THEMEN);
	}, [tm]);

	// Modal beim Verlassen der Seite schließen
	useEffect(() => {
		return () => uiStore.hideModal(aspThemaModalId);
	}, [uiStore]);

	return (
		<div className="pad-1rem">
			<div className="widget document-widget is-jpg">
				<form className="document-form is-reading is-jpg">
					<div className="document-form-body">
						<div className="document-tag">
							<div className="tag color-jpg">
								<Icon iconClass="calendar-alt" />
							</div>
						</div>
						<JpgMockActions />
						<div className="document-changes">
							<span>
								Erstellt: <strong>{formatDateLong(MOCK_ERSTELLT)}</strong>
							</span>
							<span>
								Zuletzt geändert: <strong>{formatDateLong(MOCK_GEAENDERT)}</strong>
							</span>
						</div>
						<div className="sharing">
							<div className="document-shared-with">
								Geteilt mit:
								<span className="comma-list">
									{MOCK_SHARED_WITH.map((share) => (
										<span className="comma-list-item" key={`${share.steart}-${share.name}`}>
											<strong> {share.steart}</strong> {share.name}
										</span>
									))}
								</span>
							</div>
						</div>
						<div className="document-agt"></div>
						<div className="document-intro">
							<strong>Rückblick/Ausblick</strong> <br />
							Wie war das vergangene Jahr und was nehme ich mir für das kommende Jahr vor?
						</div>
						<div className="fokusthemen">
							<div className="fokusthemen-table-container">
								<strong className="title">Fokusthemen</strong>
								<Table modifier="has-side-border last-col-right" tm={tm} />
							</div>
						</div>
						<div className="title">
							<strong>Jahresgespräch</strong>
						</div>
						<div className="document-content">
							<span className="html-content">
								<p>Das vergangene Jahr lief insgesamt über Plan, insbesondere im Bereich Vorsorge.</p>
								<p>Für das kommende Jahr liegt der Fokus auf den ausgewählten ASP-Kampagnen und der systematischen Bestandsbearbeitung.</p>
								<p>Die Personalsituation ist stabil, eine Verstärkung im Innendienst ist geplant.</p>
							</span>
						</div>
						<JpgMockUploads />
					</div>
					<div className="document-form-footer">
						<div className="modal-footer document-edit-state grid is-col-auto">
							<div className="is-left"></div>
							<div className="is-right">
								<Button className="button is-inverted is-primary">
									<span>Bearbeiten</span>
								</Button>
							</div>
						</div>
					</div>
				</form>
			</div>

			<AspThemaPotenzialModal thema={selectedThema} vm={vm} fokusKampagnen={fokusKampagnen} onToggle={onToggleFokusKampagne} />
		</div>
	);
});

interface IAspThemaPotenzialModal {
	thema?: IJpgThemaMockRow;
	vm: IAspAgtPotenzial;
	fokusKampagnen: ObservableSet<string>;
	onToggle: (campaignId: string) => void;
}

const AspThemaPotenzialModal = observer((props: IAspThemaPotenzialModal) => {
	const { thema, vm, fokusKampagnen, onToggle } = props;
	if (!thema || !thema.impuls) {
		return <></>;
	}
	const campaigns = campaignsByImpuls(thema.impuls);
	const imFokus = campaigns.filter((c) => fokusKampagnen.has(c.id)).length;
	return (
		<Modal modalId={aspThemaModalId} title={thema.def.thema} size="medium">
			<div className="pad-bottom-1rem">
				<span className="color-grey">{thema.def.byLine}</span>
			</div>
			<div className="pad-bottom-1rem asp-inline">
				<span className={`asp-kategorie-dot ${aspImpulsModifier[thema.impuls]}`} />
				<span>
					<strong>{thema.impuls}</strong> · Potenzial für {vm.agt.name}
				</span>
			</div>
			<div className="pad-bottom-1rem">
				Auswahl der Kampagnen, auf die sich die Agentur fokussieren soll:{' '}
				<strong>
					{imFokus} von {campaigns.length}
				</strong>{' '}
				im Fokus
			</div>
			<table className="table is-fullwidth">
				<thead>
					<tr>
						<th>Fokus</th>
						<th>Kampagne</th>
						<th>Kategorie</th>
						<th>Zeitraum</th>
						<th className="has-text-right">Potenzial</th>
					</tr>
				</thead>
				<tbody>
					{campaigns.map((c) => (
						<tr key={c.id}>
							<td>
								<CheckboxControl name={`fokus-${c.id}`} value={fokusKampagnen.has(c.id)} onChange={() => onToggle(c.id)} />
							</td>
							<td>
								<strong>{c.name}</strong>
							</td>
							<td>
								<span className="asp-inline">
									<span className={`asp-kategorie-dot is-small ${aspKategorieModifier[c.kategorie]}`} />
									<span>{c.kategorie}</span>
								</span>
							</td>
							<td>
								{formatAspDateShort(c.start)} – {formatAspDateShort(c.end)}
							</td>
							<td className="has-text-right">
								<AspPotenzialZelle potenzial={vm.potenziale[c.id]} isTop={vm.top5.includes(c.id)} zuweisung={vm.teilnahmen[c.id]} />
							</td>
						</tr>
					))}
				</tbody>
			</table>
		</Modal>
	);
});
